import React, { useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'
import useOffer from 'hooks/Offers/useOffer'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySkeleton from './Common/LesaPurchaseSummarySkeleton'
import LesaPurchaseSummaryTitle from './Common/LesaPurchaseSummaryTitle'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { OFFER_TYPE_TOUR } from 'constants/offer'
import LesaHotelPurchaseItem from './LesaHotelPurchaseItem'
import { canCancelOrderItem, canExtendHotelDates, canRequestChangeHotelDates, getFreeCancellationPeriodEndDate, getHotelItems } from 'supportAssistant/utils/orderUtils'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import { buildChatMessageMetaPayload } from 'supportAssistant/utils/messageUtils'
import useOrderCancellationConfigs from 'hooks/Order/useOrderCancellationConfigs'
import { getHotelChangeDatesConfig } from 'components/Common/OrderView/OrderItemSummaries/HotelItemSummary/HotelSummaryModifyBooking/HotelSummaryModifyBookingUtils'
import { ChatActions } from 'api/supportAssistant'
import { fetchExperiencesById } from 'actions/ExperienceActions'
import { fetchOffersById } from 'actions/OfferActions'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import { extractOccupantsFromReservation } from 'lib/checkout/cartReservationUtils'
import { canAddExtraGuests } from 'lib/order/extraGuestUtils'

interface Props {
  order: App.Order
  isPastOrder: boolean
  isCurrentOrder: boolean
  hideSelect?: boolean
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>
  canEdit: boolean
}

export default function LesaHotelPurchases({ order, isPastOrder, hideSelect, isCurrentOrder, componentsMap, canEdit }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()

  const [offer]: Array<any> = useOffer<App.Offer>(order.items[0]?.offerId)

  const items = getHotelItems(order)
  const item = items[0]
  const [cancellationConfig] = useOrderCancellationConfigs(item.id, order.id)

  const orderOffer = item?.offer
  const isTour = orderOffer?.parentType === OFFER_TYPE_TOUR

  const hasExperiences = !!order.experienceItems.length
  const hasTransfers = !!order.transferItems.length
  const hasAddons = !!order.addonItems.length
  const hasInsurance = !!order.insuranceItems.length
  const hasFlight = !!order.hasFlight

  const insuranceProductName = useAppSelector(state => state.geo.insuranceProductName)

  const { rates } = useOfferAvailableRates(item?.offerId, {
    checkIn: item.reservation?.startDate,
    checkOut: item.reservation?.endDate,
    rooms: extractOccupantsFromReservation(item.reservation),
  })

  const canAddGuests = canAddExtraGuests(offer, order, item, rates, { canRefund: cancellationConfig?.canRefund })

  const canChangeDates = getHotelChangeDatesConfig(
    false,
    item,
    order,
    offer,
    cancellationConfig,
  ).canDateChange
  const canRequestChangeDates = canRequestChangeHotelDates(order, offer, cancellationConfig)
  const canExtendDates = canChangeDates && canExtendHotelDates(order, offer, undefined, cancellationConfig)
  const canBeCancelled = canCancelOrderItem(order, item, cancellationConfig)
  const freeCancellationPeriodEndDate = getFreeCancellationPeriodEndDate(order, item, cancellationConfig) ?? ''

  const categoryLabel = isTour ? intl.formatMessage({
    id: 'MuglfihbdW',
    defaultMessage: 'Tour',
    description: 'Category label for tour purchase',
  }) : intl.formatMessage({
    id: '7TTiDtdcO6',
    defaultMessage: 'Hotel',
    description: 'Category label for hotel purchase',
  })

  const isListFlightsOrdersAction = !!componentsMap.get('list-flight-orders')
  const isListOrdersWithAirportTransfersAction = !!componentsMap.get('list-orders-with-airport-transfers')
  const isListOrdersWithExtraGuestsAction = !!componentsMap.get('list-orders-extra-guests')

  let action: ChatActions = ChatActions.SELECT_ORDER
  if (isListFlightsOrdersAction) {
    action = ChatActions.SELECT_FLIGHT_ORDER
  } else if (isListOrdersWithAirportTransfersAction) {
    action = ChatActions.AIRPORT_TRANSFERS_SELECT_ORDER
  } else if (isListOrdersWithExtraGuestsAction) {
    action = ChatActions.SELECT_ORDER_EXTRA_GUESTS
  }

  useEffect(() => {
    if (hasExperiences || hasTransfers) {
      const experienceIds = order.experienceItems.map(item => item.experienceId)
      const transferIds = order.transferItems.map(item => item.option.experienceId)
      const allExperienceIds = [...experienceIds, ...transferIds]

      dispatch(fetchExperiencesById(...allExperienceIds))
    }

    dispatch(fetchOffersById(order.items.map(item => item.offerId)))
  }, [hasExperiences, hasTransfers, dispatch, order.experienceItems, order.items, order.transferItems])

  const handleOrderSelect = useCallback(() => {
    dispatch(addChatMessage('', {
      action,
      payload: {
        ...buildChatMessageMetaPayload(order, item, offer, {
          isCurrentOrder,
          canRequestChangeDates,
          canExtendDates,
          checkInDate: item.reservation?.startDate,
          canChangeDates,
          canBeCancelled,
          freeCancellationPeriodEndDate,
          orderType: 'hotel',
          canAddExtraGuests: canAddGuests,
          canEditOrder: canEdit,
        }, cancellationConfig),
      },
    }))
  }, [canAddGuests, dispatch, order, item, offer, isCurrentOrder, canRequestChangeDates, canExtendDates, canChangeDates, canBeCancelled, freeCancellationPeriodEndDate, action, cancellationConfig, canEdit])

  return (
    <>
      {!offer && <LesaPurchaseSummarySkeleton />}
      {offer && <LesaPurchaseSummaryContainer>
        <LesaPurchaseSummaryHeader order={order}>
          <BodyTextBlock lineClamp={2} variant="small" startIcon={<LineBedIcon size="XS" />}>
            {categoryLabel}
            {/* TODO: i18n — concatenated string */}
            {hasFlight && ' + Flights'}
            {/* TODO: i18n — concatenated string */}
            {(hasExperiences || hasAddons) && ' + Experiences'}
            {/* TODO: i18n — concatenated string */}
            {hasInsurance && ` + Travel ${insuranceProductName}`}
          </BodyTextBlock>
          {!isPastOrder && <StatusLabel variant="confirmation">{intl.formatMessage({
            id: 'bYoBIOrt0Y',
            defaultMessage: 'Upcoming',
            description: 'Status label for upcoming hotel purchase',
          })}
          </StatusLabel>}
          {isPastOrder && <StatusLabel variant="pending">{intl.formatMessage({
            id: 'u97Bcilk35',
            defaultMessage: 'Past',
            description: 'Status label for past hotel purchase',
          })}
          </StatusLabel>}
        </LesaPurchaseSummaryHeader>
        <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
          <Group direction="vertical" gap={4}>
            {orderOffer?.location && <CaptionTextBlock
              variant="medium"
              colour="neutral-three"
            >{orderOffer.location}
            </CaptionTextBlock>}
            <LesaPurchaseSummaryTitle orderId={order.id}>
              {orderOffer?.name}
            </LesaPurchaseSummaryTitle>{' '}
            {items && <LesaHotelPurchaseItem order={order} isTour={isTour} />}
          </Group>
          {!hideSelect &&
            <TextButton onClick={handleOrderSelect} fit="mobile-full-width" size="small" kind="secondary">
              {intl.formatMessage({
                id: 'm/3B+oCK0X',
                defaultMessage: 'Select',
                description: 'Button to select a hotel purchase',
              })}
            </TextButton>}
        </Group>
      </LesaPurchaseSummaryContainer>}
    </>

  )
}
